import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Fade } from "react-awesome-reveal";
import { ArrowLeft, Check, ShoppingCart, Shield, CheckCircle2 } from "lucide-react";
import { Background } from "../../components/background";
import { Footer } from "../../components/footer";
import { getProductBySlug } from "../../config/products";
import { trackEvent } from "../../helpers";
import {
  PageWrapper,
  HeroSection,
  BackLink,
  HeroContainer,
  HeroContent,
  ProductTag,
  ProductTitle,
  ProductSubtitle,
  ProductDescription,
  PurchaseCard,
  PriceSection,
  PriceRow,
  CurrentPrice,
  FuturePrice,
  FoundingPriceNotice,
  IncludedSection,
  IncludedTitle,
  IncludedList,
  IncludedItem,
  AcknowledgmentCheckbox,
  PurchaseButton,
  SecureNotice,
  ContentSection,
  ContentContainer,
  SectionTitle,
  FeaturesGrid,
  FeatureCard,
  FeatureText,
  WhatYouGetGrid,
  WhatYouGetItem,
  WhatYouGetTitle,
  WhatYouGetDescription,
} from "./product-detail.styles";

export const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const product = slug ? getProductBySlug(slug) : undefined;

  if (!product) {
    return (
      <>
        <Background />
        <PageWrapper>
          <HeroSection>
            <ContentContainer>
              <BackLink onClick={() => navigate('/the-guide')}>
                <ArrowLeft size={16} />
                Back to Shop
              </BackLink>
              <ProductTitle>Product not found</ProductTitle>
              <ProductSubtitle>
                The product you're looking for doesn't exist or is no longer available.
              </ProductSubtitle>
            </ContentContainer>
          </HeroSection>
          <Footer />
        </PageWrapper>
      </>
    );
  }

  const handlePurchase = async () => {
    if (!acknowledged || isProcessing) return;

    setIsProcessing(true);
    trackEvent(`Purchase initiated: ${product.name}`);

    try {
      // Call Railway backend to create checkout session
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const response = await fetch(`${apiUrl}/api/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productSlug: product.slug,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Something went wrong. Please try again or contact support.');
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Background />
      <PageWrapper>
        {/* Hero Section */}
        <HeroSection>
          <HeroContainer>
            <HeroContent>
              <Fade triggerOnce>
                <BackLink onClick={() => navigate('/the-guide')}>
                  <ArrowLeft size={16} />
                  Back to Shop
                </BackLink>
              </Fade>

              <Fade delay={100} triggerOnce>
                <ProductTag>Digital Download</ProductTag>
              </Fade>

              <Fade delay={200} triggerOnce>
                <ProductTitle>{product.name}</ProductTitle>
              </Fade>

              <Fade delay={300} triggerOnce>
                <ProductSubtitle>{product.subtitle}</ProductSubtitle>
              </Fade>

              <Fade delay={400} triggerOnce>
                <ProductDescription>{product.longDescription}</ProductDescription>
              </Fade>
            </HeroContent>

            {/* Purchase Card */}
            <Fade delay={300} triggerOnce>
              <PurchaseCard>
                <PriceSection>
                  <PriceRow>
                    <CurrentPrice>£{product.currentPrice}</CurrentPrice>
                    {product.isFoundingPriceActive && (
                      <FuturePrice>£{product.futurePrice}</FuturePrice>
                    )}
                  </PriceRow>

                  {product.isFoundingPriceActive && (
                    <FoundingPriceNotice>
                      <strong>Founding launch price:</strong> This complete system is currently available for £{product.currentPrice}. The price will increase to £{product.futurePrice} after the founding launch.
                    </FoundingPriceNotice>
                  )}
                </PriceSection>

                <IncludedSection>
                  <IncludedTitle>What's Included</IncludedTitle>
                  <IncludedList>
                    {product.includedResources.map((resource, index) => (
                      <IncludedItem key={index}>
                        <CheckCircle2 size={20} />
                        {resource}
                      </IncludedItem>
                    ))}
                  </IncludedList>
                </IncludedSection>

                <AcknowledgmentCheckbox>
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  <span>
                    I understand this is a digital product. After purchase, I will receive immediate access to download all files via email.
                  </span>
                </AcknowledgmentCheckbox>

                <PurchaseButton
                  $disabled={!acknowledged || isProcessing}
                  onClick={handlePurchase}
                  disabled={!acknowledged || isProcessing}
                >
                  {isProcessing ? (
                    'Processing...'
                  ) : (
                    <>
                      <ShoppingCart size={20} />
                      Purchase Now
                    </>
                  )}
                </PurchaseButton>

                <SecureNotice>
                  <Shield size={16} />
                  Secure payment via Stripe
                </SecureNotice>
              </PurchaseCard>
            </Fade>
          </HeroContainer>
        </HeroSection>

        {/* Features Section */}
        <ContentSection>
          <ContentContainer>
            <Fade triggerOnce>
              <SectionTitle>What Makes This Different</SectionTitle>
            </Fade>

            <FeaturesGrid>
              {product.features.map((feature, index) => (
                <Fade key={index} delay={index * 50} triggerOnce>
                  <FeatureCard>
                    <Check size={24} />
                    <FeatureText>{feature}</FeatureText>
                  </FeatureCard>
                </Fade>
              ))}
            </FeaturesGrid>
          </ContentContainer>
        </ContentSection>

        {/* What You Get Section */}
        <ContentSection style={{ paddingTop: 0 }}>
          <ContentContainer>
            <Fade triggerOnce>
              <SectionTitle>What You'll Get</SectionTitle>
            </Fade>

            <WhatYouGetGrid>
              {product.whatYouGet.map((item, index) => (
                <Fade key={index} delay={index * 100} triggerOnce>
                  <WhatYouGetItem>
                    <WhatYouGetTitle>{item.title}</WhatYouGetTitle>
                    <WhatYouGetDescription>{item.description}</WhatYouGetDescription>
                  </WhatYouGetItem>
                </Fade>
              ))}
            </WhatYouGetGrid>
          </ContentContainer>
        </ContentSection>

        <Footer />
      </PageWrapper>
    </>
  );
};
